/**
 * The org's PRIMARY usage constraint — the one category closest to its limit.
 *
 * The rail is a glance surface. The question it answers is "am I about to be cut off", not "what is my
 * breakdown", so it shows one bar rather than seven; the breakdown lives at `/app/settings/usage`.
 * Returns `null` on unlimited plans, where a progress bar has no meaning, and on any failure — a
 * usage widget must never be the reason a sidebar breaks.
 *
 * See https://app.notion.com/p/3c304e9a5928811ca887eeb7452a060f §7 (W3).
 */
import type { OrgCategoryView, OrgUsageResponse } from '~~/shared/usage/orgView'

export type PrimaryUsage = OrgCategoryView

/**
 * How long a fetched value stays fresh. A one-shot "loaded" flag was wrong: the rail would show the
 * value from page load for the rest of the session, so uploading a document could never move the bar —
 * which reads as "usage isn't being counted" rather than "this widget is stale".
 */
const TTL_MS = 60_000

export function useOrgUsageMeter() {
  const { apiFetch } = useApi()
  // Shared across every component that asks, so mounting the rail twice doesn't double the request.
  const primary = useState<PrimaryUsage | null>('org-usage-primary', () => null)
  const fetchedAt = useState<number>('org-usage-primary-at', () => 0)

  async function load() {
    if (fetchedAt.value && Date.now() - fetchedAt.value < TTL_MS) return
    fetchedAt.value = Date.now()
    try {
      const data = await apiFetch<OrgUsageResponse>(
        '/api/orgs/token-usage?period=7d',
      )
      if (data.plan.tier === 'unlimited') return
      const categories = Object.values(data.plan.categories ?? {}).filter(c => c.limited)
      if (!categories.length) return
      // Closest to its limit wins — a blocked category always outranks a merely busy one.
      primary.value = categories.reduce((worst, c) => (c.percentUsed > worst.percentUsed ? c : worst))
    } catch {
      // Silent by design: the rail renders without the meter rather than showing an error in a nav.
      primary.value = null
    }
  }

  /** Force a refetch — e.g. after an upload or a chat turn that may have moved the needle. */
  async function refresh() {
    fetchedAt.value = 0
    await load()
  }

  return { primary, load, refresh }
}
